import SocialPost from "src/features/SocialFeedContent/components/SocialPost/SocialPost";
import type { SocialPostProps } from "src/features/SocialFeedContent/components/SocialPost/SocialPost";
import PostDetailDialog from "src/features/SocialFeedContent/components/PostDetailDialog/PostDetailDialog";
import { useEffect, useState } from "react";
import { useInView } from "react-intersection-observer";

export interface SocialPostListProps {
	posts?: SocialPostFull[];

	onEndReached?: () => void;

	isFetchingNextPage?: boolean;

	onHashtagClick?: (hashtag: string) => void;
}

const SocialPostList = ({
	posts,
	onEndReached,
	isFetchingNextPage = false,
	onHashtagClick
}: SocialPostListProps) => {
	const { ref, inView } = useInView({ threshold: 1 });
	const [selectedPost, setSelectedPost] = useState<SocialPostProps | null>(
		null
	);

	useEffect(() => {
		if (!inView || !onEndReached) return;

		onEndReached();
	}, [inView, onEndReached]);

	const handlePostClick = (post: SocialPostFull) => {
		setSelectedPost({
			id: post.id,
			authorId: post.author,
			authorName: post.authorName,
			authorRole: post.authorRole,
			authorAvatarUrl: post.authorAvatarUrl,
			body: post.body,
			images: post.images,
			videos: post.videos,
			hashtags: post.hashtags,
			isSponsored: post.isSponsored,
			sponsorName: post.sponsorName,
			publishedDate: post.publishedDate,
			commentsCount: post.commentsCount,
			reactionsCount: post.reactionsCount,
			userReaction: post.userReaction,
			isSaved: post.isSaved
		});
	};

	return (
		<div>
			{posts && posts.length > 0 && (
				<div data-testid="scroll-container">
					<div className="flex flex-col gap-4">
						{posts.map((post) => (
							<div
								key={post.id}
								onClick={() => handlePostClick(post)}
								className="cursor-pointer"
								role="button"
								tabIndex={0}
								onKeyDown={(e) => {
									if (e.key === "Enter" || e.key === " ") {
										e.preventDefault();
										handlePostClick(post);
									}
								}}
							>
								<SocialPost
									id={post.id}
									authorId={post.author}
									authorName={post.authorName}
									authorRole={post.authorRole}
									authorAvatarUrl={post.authorAvatarUrl}
									body={post.body}
									images={post.images}
									videos={post.videos}
									hashtags={post.hashtags}
									isSponsored={post.isSponsored}
									sponsorName={post.sponsorName}
									publishedDate={post.publishedDate}
									commentsCount={post.commentsCount}
									reactionsCount={post.reactionsCount}
									userReaction={post.userReaction}
									isSaved={post.isSaved}
									onHashtagClick={onHashtagClick}
								/>
							</div>
						))}
					</div>

					<div ref={ref} />
				</div>
			)}

			{isFetchingNextPage && (
				<div className="flex justify-center mt-8">
					<div className="flex items-center gap-2 text-sm text-zinc-500">
						<div className="w-4 h-4 border-2 border-border border-t-primary rounded-full animate-spin" />
						<span>Loading more posts</span>
					</div>
				</div>
			)}

			<PostDetailDialog
				post={selectedPost}
				isOpen={selectedPost !== null}
				onClose={() => setSelectedPost(null)}
			/>
		</div>
	);
};

export default SocialPostList;
